import {AfterViewInit, Component, OnDestroy, OnInit, Inject, Optional, ChangeDetectionStrategy} from '@angular/core';
import {FormBuilder, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {RewardsAdminService} from '../../../../services/rewards-admin/rewards-admin.service';
import {UiAlertService} from '../../../../services/ui-alert/ui-alert.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import * as L from 'leaflet';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

@Component({
    selector: 'app-superadmin-reward-detail',
    templateUrl: './superadmin-reward-detail.component.html',
    styleUrls: ['./superadmin-reward-detail.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SuperadminRewardDetailComponent implements OnInit, AfterViewInit, OnDestroy {
  public reward: any;
  public campaigns: any[] = [];
  public imageFile: File | null = null;
  public saving = false;
  private map: L.Map | null = null;
  private marker: L.Marker | null = null;
  private circle: L.Circle | null = null;
  public isDialog = false;

  public form = this.formBuilder.group({
    name: ['', Validators.required],
    textColor: ['#000000', Validators.required],
    backgroundColor: ['#ffffff', Validators.required],
    visibleExpression: ['true', Validators.required],
    bannerDetails: [{}],
    rewardDetails: [{}],
    redeemDetails: [{}],
    schedule: [{}],
    latitude: [''],
    longitude: [''],
    radius: [''],
    campaignIds: this.formBuilder.control([] as string[]),
  });

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private rewardsAdminService: RewardsAdminService,
    private uiAlertService: UiAlertService,
    private snackBar: MatSnackBar,
    @Optional() private dialogRef?: MatDialogRef<SuperadminRewardDetailComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) private dialogData?: { rewardId?: string; reward?: any },
  ) {}

  ngOnInit(): void {
    this.isDialog = !!this.dialogRef;
    this.loadCampaigns();
    this.loadReward();
    ['latitude', 'longitude', 'radius'].forEach((field) => {
      this.form.get(field)?.valueChanges.subscribe(() => this.setMapLocation());
    });
  }

  ngAfterViewInit(): void {
    this.initMap();
  }

  ngOnDestroy(): void {
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  async loadCampaigns(): Promise<void> {
    try {
      this.campaigns = await this.rewardsAdminService.getCampaigns('', 0, 10000);
    } catch (error) {
      console.error('Error loading campaigns', error);
    }
  }

  parseJsonValue(value: any): any {
    if (typeof value === 'string') {
      try {
        return value ? JSON.parse(value) : {};
      } catch {
        return value;
      }
    }
    return value ?? {};
  }

  private coalesce<T>(value: T | null | undefined, fallback: T): T {
    if (value === null || value === undefined) {
      return fallback;
    }
    if (typeof value === 'string' && value.trim() === '') {
      return fallback;
    }
    return value;
  }

  private getRewardId(reward: any): string {
    return String(reward?.id || reward?.rewardId || reward?._id || '').trim();
  }

  private normalizeRewardRecord(reward: any): any {
    if (!reward) {
      return reward;
    }

    const rewardId = this.getRewardId(reward);
    return rewardId ? { ...reward, id: rewardId } : reward;
  }

  private patchRewardForm(reward: any): void {
    if (!reward) {
      return;
    }

    const current = this.form.getRawValue();
    this.form.patchValue({
      name: this.coalesce(reward.name, current.name),
      textColor: this.coalesce(reward.textColor, current.textColor),
      backgroundColor: this.coalesce(reward.backgroundColor, current.backgroundColor),
      visibleExpression: this.coalesce(reward.visibleExpression, current.visibleExpression),
      bannerDetails: this.parseJsonValue(this.coalesce(reward.bannerDetails, current.bannerDetails)),
      rewardDetails: this.parseJsonValue(this.coalesce(reward.rewardDetails, current.rewardDetails)),
      redeemDetails: this.parseJsonValue(this.coalesce(reward.redeemDetails, current.redeemDetails)),
      schedule: this.parseJsonValue(this.coalesce(reward.schedule, current.schedule)),
    });

    const nextLatitude = reward.location ? reward.location.y : current.latitude;
    const nextLongitude = reward.location ? reward.location.x : current.longitude;
    const nextRadius = reward.location ? this.coalesce(reward.radius, current.radius) : current.radius;
    this.form.patchValue({
      latitude: nextLatitude,
      longitude: nextLongitude,
      radius: nextRadius,
    });
  }

  private hasRewardFieldChanges(): boolean {
    return [
      'name',
      'textColor',
      'backgroundColor',
      'visibleExpression',
      'bannerDetails',
      'rewardDetails',
      'redeemDetails',
      'schedule',
      'latitude',
      'longitude',
      'radius',
    ].some((controlName) => this.form.get(controlName)?.dirty);
  }

  private hasCampaignSelectionChanges(): boolean {
    return !!this.form.get('campaignIds')?.dirty;
  }

  private getErrorMessage(error: any, fallback: string): string {
    return error?.error?.error
      || error?.error?.message
      || error?.data?.error
      || error?.data?.message
      || error?.message
      || fallback;
  }

  async loadReward(): Promise<void> {
    const id = this.dialogData?.rewardId || this.getRewardId(this.dialogData?.reward) || this.route.snapshot.paramMap.get('id') || 'new';
    if (this.dialogData?.reward) {
      this.reward = this.normalizeRewardRecord(this.dialogData.reward);
      this.patchRewardForm(this.reward);
    }
    if (id === 'new') {
      return;
    }
    await this.loadRewardCampaigns(id);
    try {
      this.reward = this.normalizeRewardRecord(await this.rewardsAdminService.getRewardById(id));
      this.patchRewardForm(this.reward);
      await this.loadRewardCampaigns(this.getRewardId(this.reward));
      this.setMapLocation();
    } catch (error) {
      console.error('Error loading reward', error);
    }
  }

  private async loadRewardCampaigns(rewardId: string): Promise<void> {
    if (!rewardId) {
      return;
    }
    try {
      const campaignList = await this.rewardsAdminService.getCampaignsForReward(rewardId);
      const campaignIdsControl = this.form.get('campaignIds');
      if (campaignIdsControl?.dirty) {
        return;
      }
      const campaignIds = (campaignList || [])
        .map((campaign: any) => campaign?.id || campaign?.campaignId || campaign?._id)
        .filter(Boolean)
        .map((campaignId: any) => String(campaignId));
      this.form.patchValue({ campaignIds }, { emitEvent: false });
    } catch (error) {
      console.error('Error loading reward campaigns', error);
    }
  }

  isCampaignClosed(campaign: any): boolean {
    if (!campaign?.endAt) {
      return false;
    }
    return campaign.endAt < new Date().toISOString();
  }

  private initMap(): void {
    if (this.map) {
      return;
    }
    this.map = L.map('reward-map', {
      scrollWheelZoom: false,
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: 'Map data © OpenStreetMap contributors',
      maxZoom: 18,
    }).addTo(this.map);

    this.map.setView([43.679252, -70.368457], 10);
    this.setMapLocation();
  }

  private setMapLocation(): void {
    if (!this.map) {
      return;
    }
    const lat = parseFloat(this.form.get('latitude')?.value || '');
    const lng = parseFloat(this.form.get('longitude')?.value || '');
    const radius = parseFloat(this.form.get('radius')?.value || '');

    if (Number.isNaN(lat) || Number.isNaN(lng)) {
      return;
    }

    const location: L.LatLngExpression = [lat, lng];
    if (!this.marker) {
      this.marker = L.marker(location).addTo(this.map);
    } else {
      this.marker.setLatLng(location);
    }

    if (this.circle) {
      this.map.removeLayer(this.circle);
      this.circle = null;
    }
    if (!Number.isNaN(radius) && radius > 0) {
      this.circle = L.circle(location, {
        color: 'red',
        fillColor: '#f03',
        fillOpacity: 0.3,
        radius,
      }).addTo(this.map);
    }

    const zoom = this.convertRadiusToZoom(radius);
    this.map.setView(location, zoom);
  }

  private convertRadiusToZoom(radius?: number): number {
    if (!radius || Number.isNaN(radius)) {
      return 12;
    }
    const base = 18 - Math.log2(radius / 50);
    return Math.max(3, Math.min(18, Math.round(base)));
  }

  onFileChange(event: any): void {
    this.imageFile = event.target.files?.[0] || null;
  }

  toJsonValue(value: any): any {
    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    }
    return value;
  }

  async save(): Promise<void> {
    if (this.form.invalid) {
      return;
    }
    this.saving = true;
    const payload: any = {
      name: this.form.get('name')?.value,
      textColor: this.form.get('textColor')?.value,
      backgroundColor: this.form.get('backgroundColor')?.value,
      visibleExpression: this.form.get('visibleExpression')?.value,
      bannerDetails: this.toJsonValue(this.form.get('bannerDetails')?.value),
      rewardDetails: this.toJsonValue(this.form.get('rewardDetails')?.value),
      redeemDetails: this.toJsonValue(this.form.get('redeemDetails')?.value),
      schedule: this.toJsonValue(this.form.get('schedule')?.value),
    };

    const latitude = this.form.get('latitude')?.value;
    const longitude = this.form.get('longitude')?.value;
    const radius = this.form.get('radius')?.value;
    if (latitude && longitude) {
      payload.location = { x: parseFloat(longitude), y: parseFloat(latitude) };
      payload.radius = radius ? parseFloat(radius) : undefined;
    }

    try {
      if (this.reward) {
        const rewardId = this.getRewardId(this.reward);
        payload.id = rewardId;
        if (this.hasRewardFieldChanges()) {
          await this.rewardsAdminService.putReward(payload);
        }
        if (this.hasCampaignSelectionChanges()) {
          await this.rewardsAdminService.putCampaignsForReward(rewardId, this.form.get('campaignIds')?.value || []);
        }
        if (this.imageFile) {
          await this.rewardsAdminService.changeRewardImage(rewardId, this.imageFile);
        }
        this.snackBar.open('Reward successfully updated', 'Close', { duration: 2000 });
      } else {
        const result = await this.rewardsAdminService.postReward(payload);
        await this.rewardsAdminService.putCampaignsForReward(result.id, this.form.get('campaignIds')?.value || []);
        if (this.imageFile) {
          await this.rewardsAdminService.changeRewardImage(result.id, this.imageFile);
        }
        this.snackBar.open('Reward successfully created', 'Close', { duration: 2000 });
      }
      if (this.dialogRef) {
        this.dialogRef.close(true);
      } else {
        this.router.navigate(['/superadmin/rewards/rewards']).then();
      }
    } catch (error) {
      console.error('Error saving reward', error);
      this.snackBar.open(this.getErrorMessage(error, 'Unable to save reward'), 'Close', { duration: 4000 });
    } finally {
      this.saving = false;
    }
  }

  async delete(): Promise<void> {
    if (!this.reward) {
      return;
    }
    const confirm = await this.uiAlertService.presentAlertConfirm('Delete this reward?');
    if (!confirm) {
      return;
    }
    try {
      await this.rewardsAdminService.deleteReward(this.reward);
      this.snackBar.open('Reward successfully deleted', 'Close', { duration: 2000 });
      if (this.dialogRef) {
        this.dialogRef.close(true);
      } else {
        this.router.navigate(['/superadmin/rewards/rewards']).then();
      }
    } catch (error) {
      console.error('Error deleting reward', error);
    }
  }

  cancel(): void {
    if (this.dialogRef) {
      this.dialogRef.close(false);
    } else {
      this.router.navigate(['/superadmin/rewards/rewards']).then();
    }
  }
}
